import { Navigate, Route, Routes } from 'react-router';

import { useSession } from '../../contexts/session';
import { navigation } from '../../hooks/navigation';

import { Centered } from '../centered';
import { CheckCode } from '../check-code';
import { DailyRecord } from '../daily-record';
import { AssignTest } from '../assign-test';
import { Dashboard } from '../dashboard';
import { Diary } from '../diary';
import { Frame } from '../frame';
import { Invitation } from '../invitation';
import { InvitePatient } from '../invite-patient';
import { Landing } from '../landing';
import { LoadFailed } from '../load-failed';
import { Onboarding, steps } from '../onboarding';
import { PatientDiary } from '../patient-diary';
import { PatientTests } from '../patient-tests';
import { Patients } from '../patients';
import { RememberPath } from '../remember-path';
import { ReviewAttempt } from '../review-attempt';
import { ReturnToPath } from '../return-to-path';
import { Settings } from '../settings';
import { SignIn } from '../sign-in';
import { TestAttempt } from '../test-attempt';
import { Tests } from '../tests';
import { Today } from '../today';

// Each section's screen, by role and path. The paths and their order come
// from the navigation the top bar reads, so a section cannot have a link
// without a screen or a screen without a link.
const screens = {
  PATIENT: {
    '/': <Today/>,
    '/diary': <Diary/>,
    '/tests': <Tests/>
  },
  CLINICIAN: {
    '/': <Dashboard/>,
    '/patients': <Patients/>
  }
};

// The pages inside each role's sections, reached from those sections rather
// than from the bar.
const pages = {
  PATIENT: [
    <Route key='record' path='/diary/record/:date/:step' element={ <DailyRecord/> }/>,
    <Route key='attempt' path='/tests/:attemptId/:step' element={ <TestAttempt/> }/>,
    <Route key='settings' path='/settings' element={ <Settings/> }/>,
    <Route key='invitation' path='/invitations/:invitationId' element={ <Invitation/> }/>
  ],
  CLINICIAN: [
    <Route key='invite' path='/patients/invite' element={ <InvitePatient/> }/>,
    <Route key='patient' path='/patients/:patientId' element={ <Navigate to='tests' replace/> }/>,
    <Route key='patient-tests' path='/patients/:patientId/tests' element={ <PatientTests/> }/>,
    <Route key='assign' path='/patients/:patientId/assign' element={ <AssignTest/> }/>,
    <Route key='review' path='/patients/:patientId/tests/:attemptId' element={ <ReviewAttempt/> }/>,
    <Route key='patient-diary' path='/patients/:patientId/diary' element={ <PatientDiary/> }/>,
    <Route key='settings' path='/settings' element={ <Settings/> }/>
  ]
};

// Four route tables, chosen by who is signed in. A signed-out visitor has no
// route to the app at all, rather than a route that checks and turns them
// away: only the landing page at the root and the sign-in screens. Someone
// part-way through onboarding has only the onboarding steps; everyone else has their role's sections, a patient's or a clinician's.
// Each table sends a path it does not know to its own starting point, so a
// stale link never dead-ends. A link a signed-out visitor followed is kept
// and opened once they are in, so an invitation link survives sign-in and
// onboarding.
//
// Nothing renders until the first answer about the session arrives, so a
// signed-in person never sees the sign-in screen flash past. When that answer
// cannot be had, the visitor is neither signed in nor out: they see that it
// failed and can ask again.
//
// The server answers every path with the same document, which is what lets
// this table decide. The redirects replace rather than push, so Back leaves
// psy instead of bouncing between a redirect and the page it points at.
export function Router() {
  const { me, error, retry } = useSession();

  if (error != null) {
    return (
      <Centered>
        <LoadFailed onRetry={ retry }/>
      </Centered>
    );
  }

  if (me === undefined) {
    return null;
  }

  if (me === null) {
    return (
      <Routes>
        <Route path='/' element={ <Landing/> }/>
        <Route path='/signin' element={ <SignIn/> }/>
        <Route path='/signin/code' element={ <CheckCode/> }/>
        <Route path='*' element={ <RememberPath/> }/>
      </Routes>
    );
  }

  if (me.onboardingStep !== null) {
    return (
      <Routes>
        <Route path='/onboarding/:step' element={ <Onboarding/> }/>
        <Route path='*' element={ <Navigate to={ `/onboarding/${ steps[ me.onboardingStep - 1 ] }` } replace/> }/>
      </Routes>
    );
  }

  const { home, sections } = navigation[ me.role ];

  const routes = [ home ].concat(sections).map(function({ path }) {
    return <Route key={ path } path={ path } element={ screens[ me.role ][ path ] }/>;
  });

  // ReturnToPath comes after the routes, so its navigation runs after the
  // fallback's redirect to the home page and has the last word.
  return (
    <>
      <Routes>
        <Route element={ <Frame/> }>
          { routes }

          { pages[ me.role ] }
        </Route>

        <Route path='*' element={ <Navigate to={ home.path } replace/> }/>
      </Routes>

      <ReturnToPath/>
    </>
  );
}
